// src/common/components/RefundPopup.tsx
import { useEffect, useState } from "react";
import { Popover, Box, Button, RadioGroup, FormControlLabel, Radio, TextField, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";

interface RefundPopupProps {
    /**
     * Whether the popup is open
     */
    open: boolean;

    /**
     * The HTML element to anchor the popup to
     */
    anchorEl: HTMLElement | null;

    /**
     * Callback to close the popup
     */
    onClose: () => void;

    /**
     * Callback fired when user confirms refund action
     * @param refundType - full or partial
     * @param amount - optional refund amount (required for partial)
     */
    onConfirm: (refundType: "full" | "partial", amount?: number) => void;

    /**
     * Maximum refundable amount (transaction amount)
     */
    maxAmount: number;

    /**
     * Optional flag to show loading state when API call is in progress
     */
    loading?: boolean;

    /**
     * Already refunded amount
     */
    refundedAmount: number;

    /**
     * Percentage surcharge fee applied on the transaction
     */
    surchargeFeePercentage: number;
}

/**
 * RefundPopup Component
 *
 * Renders a popover UI to allow users to initiate a **full** or **partial** refund.
 * - Full Refund: refunds the entire remaining refundable amount.
 * - Partial Refund: allows user to enter a specific refund amount (validated).
 *
 * Includes:
 * - Radio selection between full and partial
 * - Input field for partial amount (validated up to 2 decimals)
 * - Loader shown while refund is processing
 * - Confirm/Cancel buttons
 */
export const RefundPopup = ({
    open,
    anchorEl,
    onClose,
    onConfirm,
    maxAmount,
    loading,
    refundedAmount = 0,
    surchargeFeePercentage = 0
}: RefundPopupProps) => {
    const [refundType, setRefundType] = useState<"full" | "partial">("full");
    const [amount, setAmount] = useState<number>(0);
    const [isProcessingRefund, setIsProcessingRefund] = useState<boolean>(false);
    
    // Calculate already refunded amount excluding surcharge fee
    const feePercentage = (surchargeFeePercentage ?? 0) / 100;
    const amountAlreadyRefunded = Math.abs(refundedAmount / (1 + feePercentage));

    /**
     * Reset state whenever the popup opens
     */
    useEffect(() => {
        if (open) {
            setRefundType("full");
            setIsProcessingRefund(false);
            setAmount(0);
        }
    }, [open]);
    
    /**
     * Handles confirm action
     * - Validates partial refund amount
     * - Calls `onConfirm` callback
     */
    const handleConfirm = () => {
        if (refundType === "partial") {
            if (typeof amount !== "number" || amount <= 0 || amount > Number((maxAmount - amountAlreadyRefunded).toFixed(2))) {
                toast.info(`Amount must be between 0.01 and ${Number((maxAmount - amountAlreadyRefunded).toFixed(2))}`);
                return;
            }
            onConfirm("partial", amount);
        } else {
            onConfirm("full", 0);
        }
        setIsProcessingRefund(true);
    };

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            //onClose={onClose}
            anchorOrigin={{ vertical: "center", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
            <Box sx={{ p: 2, width: 320 }}>
                <Box sx={{ mb: 2 }}>Would you like to do:</Box>

                {/* Refund type radio buttons */}
                <RadioGroup
                    value={refundType}
                    onChange={(e) => setRefundType(e.target.value as "full" | "partial")}
                >
                    <FormControlLabel disabled={isProcessingRefund} value="full" control={<Radio />} label="Full Refund" />
                    <FormControlLabel disabled={isProcessingRefund} value="partial" control={<Radio />} label="Partial Refund" />
                </RadioGroup>

                {/* Partial refund input */}
                {refundType === "partial" && (
                    <TextField
                        disabled={isProcessingRefund}            
                        type="number"
                        label="Refund Amount"
                        value={amount}
                        onChange={(e) => {
                            const value = e.target.value;
                            // Allow up to 2 decimal places
                            if (/^\d*\.?\d{0,2}$/.test(value)) {
                                setAmount(Number(value));
                            }
                        }}
                        inputProps={{ min: 0.01, max: Number((maxAmount - amountAlreadyRefunded).toFixed(2)), step: 0.01 }}
                        fullWidth
                        sx={{ mt: 2 }}
                    />
                )}

                {/* Action buttons */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent:'space-between' }}>
                    <Button
                        disabled={isProcessingRefund}
                        variant="contained"
                        color="primary"
                        onClick={handleConfirm}
                        startIcon={
                            isProcessingRefund ? <CircularProgress size={18} color="inherit" /> : null
                        }                        
                    >
                        {isProcessingRefund ? 'Processing...' : 'Confirm'}
                    </Button>
                    <Button
                        disabled={isProcessingRefund}        
                        variant="outlined"
                        onClick={onClose}
                        sx={{ ml: 2 }}
                    >
                        Cancel
                    </Button>
                </Box>
            </Box>
        </Popover>
    );
};
